import { useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { Badge, Card, CardBody } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import type { AutoAssignConflict, AutoAssignResult } from '@/api/projects';

interface ResultStepProps {
  result: AutoAssignResult;
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardBody>
        <div style={{ fontSize: 13, color: 'var(--vn-fg-muted)' }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </div>
      </CardBody>
    </Card>
  );
}

export function ResultStep({ result }: ResultStepProps) {
  const conflictColumns = useMemo<ColumnDef<AutoAssignConflict, unknown>[]>(
    () => [
      {
        id: 'resource_id',
        header: 'Resource',
        accessorKey: 'resource_id',
        cell: ({ getValue }) => (
          <span style={{ fontWeight: 500 }}>{getValue() as string}</span>
        ),
      },
      {
        id: 'resource_type',
        header: 'Type',
        accessorKey: 'resource_type',
        cell: ({ getValue }) => (
          <Badge tone="neutral" appearance="outline" size="sm">
            {getValue() as string}
          </Badge>
        ),
      },
      {
        id: 'project_name',
        header: 'Also in project',
        accessorKey: 'project_name',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>
            {row.original.project_name || row.original.project_id}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Alert tone="success" icon={<CheckCircle2 size={16} />} title="Resources assigned">
        {result.assigned} resource{result.assigned === 1 ? '' : 's'} assigned to this project.
      </Alert>

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <Stat label="Matched" value={result.matched} />
        <Stat label="Assigned" value={result.assigned} />
        <Stat label="Already in project" value={result.skipped_already_in_project} />
      </div>

      {result.conflicts.length > 0 ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            Also assigned to other projects ({result.conflicts.length})
          </div>
          <div style={{ fontSize: 13, color: 'var(--vn-fg-muted)' }}>
            These resources were assigned here too — a resource can belong to multiple projects.
          </div>
          <DataTable
            columns={conflictColumns}
            data={result.conflicts}
            getRowId={(c) => `${c.resource_type}:${c.resource_id}`}
            enableSorting={false}
          />
        </div>
      ) : null}
    </div>
  );
}
